import { useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { AlbumArt } from '../../shared/components/AlbumArt';
import { CheckIcon, SpeakerIcon } from '../../shared/components/icons';
import { Grain, Wordmark } from '../../shared/components/Lounge';

export function ThankYou() {
  const { state } = useLocation();
  const sent      = state?.sent ?? [];
  const reduce    = useReducedMotion();

  // Entrada escalonada: cada bloque aparece un poco después del anterior.
  const rise = (delay) => reduce ? {} : {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
  };

  return (
    <div className="relative min-h-dvh overflow-x-hidden bg-lounge-base font-inter text-lounge-text antialiased
                    selection:bg-lounge-brass/30">
      <Grain />

      <div className="relative mx-auto flex max-w-[480px] flex-col gap-10 px-5 pb-[calc(2.5rem+env(safe-area-inset-bottom))]
                      pt-[calc(1.5rem+env(safe-area-inset-top))] lg:max-w-[560px] lg:pt-12">
        <header className="flex justify-center">
          <Wordmark className="text-[13px]" />
        </header>

        {/* Hero */}
        <motion.section {...rise(0)} className="relative flex flex-col items-center text-center">
          <div
            aria-hidden
            className="pointer-events-none absolute -top-10 h-64 w-64 rounded-full bg-lounge-brass/[0.07] blur-[80px]"
          />
          <span className="relative mb-4 inline-flex items-center gap-2 rounded-full border border-lounge-line
                           bg-lounge-card px-3 py-1 font-mono text-[11px] uppercase tracking-[0.2em] text-lounge-brass">
            <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-felt" />
            Fin de tu selección
          </span>
          <h1 className="relative font-serif text-[40px] font-semibold leading-[1.1] tracking-tight text-lounge-text
                         lg:text-5xl">
            Gracias por<br />
            <span className="italic text-lounge-brass">la música.</span>
          </h1>
          <p className="relative mt-4 max-w-[300px] text-base leading-relaxed text-lounge-stone">
            Todas tus canciones ya sonaron en el local.
          </p>
        </motion.section>

        {sent.length > 0 && (
          <motion.section {...rise(0.15)} className="flex flex-col gap-3">
            <ul className="flex flex-col divide-y divide-lounge-line overflow-hidden rounded-lg border
                           border-lounge-line bg-lounge-card">
              {sent.map((s, i) => (
                <li key={s.uri ?? i} className="flex items-center gap-3.5 p-3.5">
                  <AlbumArt image={s.image} alt="" className="w-12" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium leading-tight text-lounge-text">{s.name}</p>
                    <p className="mt-0.5 truncate text-sm text-lounge-stone">{s.artist}</p>
                  </div>
                  <span className="flex shrink-0 items-center gap-1 font-mono text-[11px] uppercase tracking-wider text-felt">
                    <CheckIcon className="h-4 w-4" />
                    Sonó
                  </span>
                </li>
              ))}
            </ul>
          </motion.section>
        )}

        {/* CTA para dueños de locales */}
        <motion.section
          {...rise(0.3)}
          className="relative overflow-hidden rounded-lg border border-lounge-brass/30 bg-lounge-raised p-6 shadow-sleeve"
        >
          <span aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-lounge-brass" />
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-serif text-xl font-medium text-lounge-text">¿Quieres sofisticar tu local?</h3>
            <SpeakerIcon className="h-6 w-6 shrink-0 text-lounge-brass" />
          </div>
          <p className="mt-2 text-sm leading-relaxed text-lounge-stone">
            Deja que tus clientes elijan la música, tú mantienes el control.
          </p>
          <a
            href="/"
            className="mt-6 inline-flex h-12 w-full items-center justify-center rounded bg-lounge-brass text-sm
                       font-semibold text-lounge-base transition hover:bg-amber hover:shadow-brass active:scale-[0.98]"
          >
            Crea tu propia sala →
          </a>
        </motion.section>

        <footer className="text-center font-mono text-[10px] uppercase tracking-[0.22em] text-lounge-stone">
          powered by RockCola
        </footer>
      </div>
    </div>
  );
}
